/**
 * Helper for building per-route TanStack Router `head()` metadata so
 * server-rendered HTML carries complete SEO/OG/Twitter tags before hydration.
 */

const SITE_URL = "https://www.enno-und-jens.de";
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 155;

function truncate(value: string, max: number) {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

export interface RouteHeadOptions {
  title: string;
  description: string;
  path: string; // e.g. "/leistungen" or "/ratgeber/foo"
  image?: string; // absolute or root-relative path
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  ogType?: "website" | "article" | "profile" | "product";
  noIndex?: boolean;
  scripts?: Array<{
    type: string;
    children: string;
  }>;
}

export function buildRouteHead(opts: RouteHeadOptions) {
  const title = truncate(opts.title, MAX_TITLE_LENGTH);
  const description = truncate(opts.description, MAX_DESCRIPTION_LENGTH);
  const url = `${SITE_URL}${opts.path}`;
  const image =
    opts.image &&
    (opts.image.startsWith("http") ? opts.image : `${SITE_URL}${opts.image}`);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: opts.ogType ?? "website" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
  ];

  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ name: "twitter:image", content: image });
    if (opts.imageAlt) {
      meta.push({ property: "og:image:alt", content: opts.imageAlt });
    }
    if (opts.imageWidth) {
      meta.push({ property: "og:image:width", content: String(opts.imageWidth) });
    }
    if (opts.imageHeight) {
      meta.push({ property: "og:image:height", content: String(opts.imageHeight) });
    }
  }

  if (opts.noIndex) {
    meta.push({ name: "robots", content: "noindex,nofollow" });
  }

  return {
    meta,
    // Nicht indexierbare Seiten (404 etc.) bekommen keinen Canonical.
    ...(opts.noIndex ? {} : { links: [{ rel: "canonical", href: url }] }),
    ...(opts.scripts ? { scripts: opts.scripts } : {}),
  };
}
